import {
  CHART_INPUT_REQUIREMENTS,
  type DataType,
  dataTypeToSavedQueryDataTypes,
  type SavedQueryDataType,
} from '@revelio/core';

import type {
  SavedQueryDto,
  SavedQueryFieldDto,
} from '../../../api/saved-queries-api';
import type { FilterId } from '../../../filters/types/filters';
import type {
  ChartType,
  SelectableChartType,
} from '../../PageConfigProvider/types/chart';
import type { SnowflakeConfigField } from '../../PageConfigProvider/types/item';

function mergeCompatibleFilterIds(
  fields: SavedQueryFieldDto[]
): FilterId[] | undefined {
  const ids = new Set<string>();
  for (const f of fields) {
    if (f.compatibleFilterIds) {
      for (const id of f.compatibleFilterIds) {
        ids.add(id);
      }
    }
  }
  if (ids.size === 0) return undefined;
  return [...ids] as FilterId[];
}

const uniqueSavedQueryTypes = (
  types: SavedQueryDataType[]
): SavedQueryDataType[] => [...new Set(types)];

const DERIVED_CHART_COMPATIBILITY: Partial<
  Record<SelectableChartType, SavedQueryDataType[]>
> = Object.fromEntries(
  (
    Object.entries(CHART_INPUT_REQUIREMENTS) as [
      SelectableChartType,
      DataType[],
    ][]
  ).map(([chartType, dataTypes]) => [
    chartType,
    uniqueSavedQueryTypes(
      dataTypes.flatMap((dt) => dataTypeToSavedQueryDataTypes(dt))
    ),
  ])
);

const SAVED_QUERY_CHART_COMPATIBILITY: Partial<
  Record<ChartType, SavedQueryDataType[]>
> = {
  ...DERIVED_CHART_COMPATIBILITY,
  metric: ['ONE_DIMENSION'],
  icicle: [],
};

function getCompatibleSavedQueryDataTypes(
  chartType: ChartType | undefined
): SavedQueryDataType[] {
  if (!chartType) {
    return [];
  }
  return SAVED_QUERY_CHART_COMPATIBILITY[chartType] ?? [];
}

export function savedQueryMetricValue(query: SavedQueryDto): string {
  return query.metricId ?? `sq:${query.id}`;
}

export function findSavedQueryByMetricValue(
  queries: SavedQueryDto[],
  metricValue: string
): SavedQueryDto | undefined {
  return queries.find((q) => savedQueryMetricValue(q) === metricValue);
}

export function filterSavedQueriesForChart({
  queries,
  chartType,
  parentId,
}: {
  queries: SavedQueryDto[];
  chartType: ChartType | undefined;
  parentId: string | undefined;
}): SavedQueryDto[] {
  const compatibleDataTypes = getCompatibleSavedQueryDataTypes(chartType);
  return queries.filter((query) => {
    const queryDataType = query.dataType ?? 'TABLE';
    if (!compatibleDataTypes.includes(queryDataType)) {
      return false;
    }
    if (query.reportId === undefined || query.reportId === '') {
      return true;
    }
    return query.reportId === parentId;
  });
}

export function fieldsFromSavedQueryDto(
  query: SavedQueryDto
): SnowflakeConfigField[] {
  const grouped = new Map<string, SavedQueryFieldDto[]>();
  const ungrouped: SavedQueryFieldDto[] = [];

  for (const f of query.fields) {
    if (f.fieldGroup !== undefined && f.fieldGroup !== '') {
      const existing = grouped.get(f.fieldGroup) ?? [];
      existing.push(f);
      grouped.set(f.fieldGroup, existing);
    } else {
      ungrouped.push(f);
    }
  }

  const result: SnowflakeConfigField[] = [];

  for (const [groupKey, fields] of grouped) {
    const first = fields[0];
    if (first === undefined) continue;
    const firstWithDesc = fields.find(
      (f) => f.description !== undefined && f.description !== ''
    );
    const slotRole = fields.find((f) => f.slotRole)?.slotRole;
    const compatibleFilterIds = mergeCompatibleFilterIds(fields);
    result.push({
      name: groupKey,
      label: firstWithDesc?.description ?? groupKey,
      value: '',
      required: fields.some((f) => f.required === true),
      fieldType: first.type,
      subFields: fields.map((f) => ({
        name: f.name,
        fieldType: f.type,
        selectionLevelIds: f.selectionLevelIds ?? [],
        extractMode: f.extractMode,
      })),
      ...(slotRole !== undefined && { slotRole }),
      ...(compatibleFilterIds !== undefined && { compatibleFilterIds }),
    });
  }

  for (const f of ungrouped) {
    const compatibleFilterIds =
      f.compatibleFilterIds !== undefined && f.compatibleFilterIds.length > 0
        ? (f.compatibleFilterIds as FilterId[])
        : undefined;
    result.push({
      name: f.name,
      label: f.description ?? f.name,
      value: '',
      required: f.required ?? true,
      fieldType: f.type,
      selectionLevelIds: f.selectionLevelIds,
      ...(f.slotRole !== undefined && { slotRole: f.slotRole }),
      ...(compatibleFilterIds !== undefined && { compatibleFilterIds }),
    });
  }

  return result;
}
